/**
 * Default permission templates/presets for roles
 */

import type { PermissionTemplate } from './rolePermission'
import type { UserRole } from './user'

/**
 * Default permission templates available in the system
 */
export const PERMISSION_TEMPLATES: PermissionTemplate[] = [
  {
    id: 'admin-full',
    name: 'Admin - Full Access',
    description: 'Full system access with all permissions',
    permissions: ['*', 'audit:read'],
    category: ['System'],
  },
  {
    id: 'doctor-clinical',
    name: 'Doctor - Clinical Access',
    description: 'Full clinical access to patients, appointments, templates, and lab results',
    permissions: [
      'patients:read',
      'patients:write',
      'reminders:read',
      'reminders:write',
      'reminder-rules:read',
      'reminder-rules:write',
      'appointments:read',
      'appointments:write',
      'templates:read',
      'templates:write',
      'lab-results:read',
      'lab-results:write',
    ],
    category: ['Patients', 'Appointments', 'Templates', 'Lab Results'],
  },
  {
    id: 'nurse-clinical',
    name: 'Nurse - Clinical Access',
    description: 'Full clinical access to patients, appointments, templates, and lab results',
    permissions: [
      'patients:read',
      'patients:write',
      'reminders:read',
      'reminders:write',
      'reminder-rules:read',
      'reminder-rules:write',
      'appointments:read',
      'appointments:write',
      'templates:read',
      'templates:write',
      'lab-results:read',
      'lab-results:write',
    ],
    category: ['Patients', 'Appointments', 'Templates', 'Lab Results'],
  },
  {
    id: 'receptionist-admin',
    name: 'Receptionist - Administrative',
    description: 'Full access to patients and appointments for scheduling',
    permissions: [
      'patients:read',
      'patients:write',
      'reminders:read',
      'reminders:write',
      'reminder-rules:read',
      'appointments:read',
      'appointments:write',
      'templates:read',
    ],
    category: ['Patients', 'Appointments', 'Templates'],
  },
  {
    id: 'physician-assistant-clinical',
    name: 'Physician Assistant - Clinical',
    description: 'Clinical access similar to doctors',
    permissions: [
      'patients:read',
      'patients:write',
      'reminders:read',
      'reminders:write',
      'reminder-rules:read',
      'reminder-rules:write',
      'appointments:read',
      'appointments:write',
      'templates:read',
      'templates:write',
      'lab-results:read',
      'lab-results:write',
    ],
    category: ['Patients', 'Appointments', 'Templates', 'Lab Results'],
  },
  {
    id: 'therapist-clinical',
    name: 'Therapist - Clinical',
    description: 'Clinical access for therapy services',
    permissions: [
      'patients:read',
      'patients:write',
      'reminders:read',
      'reminders:write',
      'reminder-rules:read',
      'appointments:read',
      'appointments:write',
      'templates:read',
      'templates:write',
      'lab-results:read',
    ],
    category: ['Patients', 'Appointments', 'Templates', 'Lab Results'],
  },
  {
    id: 'billing-admin',
    name: 'Billing - Financial',
    description: 'Access to billing and financial information',
    permissions: [
      'patients:read',
      'appointments:read',
      'billing-accounts:read',
      'billing-accounts:write',
      'billing-items:read',
      'billing-items:write',
      'billing-payments:read',
      'billing-payments:write',
      'billing-statements:read',
      'billing-statements:write',
      'settings:read',
    ],
    category: ['Patients', 'Appointments', 'Billing', 'Settings'],
  },
  {
    id: 'office-manager-admin',
    name: 'Office Manager - Administrative',
    description: 'Administrative access to manage clinic operations',
    permissions: [
      'patients:read',
      'patients:write',
      'appointments:read',
      'appointments:write',
      'users:read',
      'settings:read',
      'settings:write',
    ],
    category: ['Patients', 'Appointments', 'Users', 'Settings'],
  },
  {
    id: 'system-admin-full',
    name: 'System Admin - Full System',
    description: 'Full system administrative access',
    permissions: ['*'],
    category: ['System'],
  },
  {
    id: 'it-support-readonly',
    name: 'IT Support - Read-Only',
    description: 'Read-only access for troubleshooting',
    permissions: [
      'patients:read',
      'appointments:read',
      'users:read',
      'settings:read',
    ],
    category: ['Patients', 'Appointments', 'Users', 'Settings'],
  },
]

/**
 * Default permissions for each role (used as initial templates)
 */
export const DEFAULT_ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  'admin': ['*', 'audit:read'],
  'doctor': [
    'patients:read',
    'patients:write',
    'reminders:read',
    'reminders:write',
    'reminder-rules:read',
    'reminder-rules:write',
    'appointments:read',
    'appointments:write',
    'templates:read',
    'templates:write',
    'lab-results:read',
    'lab-results:write',
  ],
  'nurse': [
    'patients:read',
    'patients:write',
    'reminders:read',
    'reminders:write',
    'reminder-rules:read',
    'reminder-rules:write',
    'appointments:read',
    'appointments:write',
    'templates:read',
    'templates:write',
    'lab-results:read',
    'lab-results:write',
  ],
  'receptionist': [
    'patients:read',
    'patients:write',
    'reminders:read',
    'reminders:write',
    'reminder-rules:read',
    'appointments:read',
    'appointments:write',
    'templates:read',
  ],
  'physician-assistant': [
    'patients:read',
    'patients:write',
    'reminders:read',
    'reminders:write',
    'reminder-rules:read',
    'reminder-rules:write',
    'appointments:read',
    'appointments:write',
    'templates:read',
    'templates:write',
    'lab-results:read',
    'lab-results:write',
  ],
  'therapist': [
    'patients:read',
    'patients:write',
    'reminders:read',
    'reminders:write',
    'reminder-rules:read',
    'appointments:read',
    'appointments:write',
    'templates:read',
    'templates:write',
    'lab-results:read',
  ],
  'billing': [
    'patients:read',
    'appointments:read',
    'billing-accounts:read',
    'billing-accounts:write',
    'billing-items:read',
    'billing-items:write',
    'billing-payments:read',
    'billing-payments:write',
    'billing-statements:read',
    'billing-statements:write',
    'settings:read',
  ],
  'office-manager': [
    'patients:read',
    'patients:write',
    'appointments:read',
    'appointments:write',
    'users:read',
    'settings:read',
    'settings:write',
  ],
  'system-admin': ['*'],
  'it-support': [
    'patients:read',
    'appointments:read',
    'users:read',
    'settings:read',
  ],
}
